// Cache for onboarding status to avoid excessive API calls
// Use a Map to cache per user to handle multiple users
const onboardingStatusCache = new Map<string, {
    data: any;
    timestamp: number;
}>();

const CACHE_DURATION = 300000; // 5 minutes cache

export default defineNuxtRouteMiddleware(async (to, from) => {
    // Only run on client side to avoid SSR issues
    if (process.server) {
        return;
    }

    // Skip middleware for the onboarding page itself
    if (to.path === '/app/onboarding') {
        return;
    }

    // Only apply to /app/* routes (excluding the onboarding page)
    if (!to.path.startsWith('/app')) {
        return;
    }

    // Check if user is logged in
    const { userId } = useAuth();


    // Use userId as cache key
    const cacheKey = userId.value || 'unknown';

    try {
        // Check cache first
        const now = Date.now();
        const cached = onboardingStatusCache.get(cacheKey);
        
        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
            console.log({ msg: 'using cached onboarding status', ts: new Date().toISOString() });
            const cachedData = cached.data;
            if (cachedData.hasSubmitted) {
                return;
            }
            if (!cachedData.hasOnboarded) {
                return navigateTo('/app/onboarding');
            }
            return;
        }

        // Check onboarding status
        const { getToken } = useAuth();
        const token = await getToken.value();
        
        const before = Date.now();
        console.log({ msg: 'fetching onboarding status', ts: new Date(before).toISOString() });
        const response = await $fetch('/api/check-onboarding', {
            headers: token ? {
                Authorization: `Bearer ${token}`
            } : {}
        });
        const after = Date.now();
        console.log({ msg: 'fetched onboarding status', ts: new Date(after).toISOString(), diff: after - before });
        
        if (response.success && response.data) {
            // Cache the response
            onboardingStatusCache.set(cacheKey, {
                data: response.data,
                timestamp: now
            });

            // Clean up old cache entries (keep only last 10 users)
            if (onboardingStatusCache.size > 10) {
                const firstKey = onboardingStatusCache.keys().next().value;
                onboardingStatusCache.delete(firstKey);
            }

            if (response.data.hasSubmitted) {
                return;
            }
            // If user hasn't completed onboarding, redirect to onboarding page
            if (!response.data.hasOnboarded) {
                return navigateTo('/app/onboarding');
            }
        }
    } catch (error) {
        console.error('Error checking onboarding status in middleware:', error);
        // On error, allow navigation to continue to avoid blocking users
    }
});
